import { Loader2, Shield, Trash2, UserPlus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { DataLabel, GrantResponse, LabelPermission } from '@/services/api/lake'
import { ColorDot } from './ColorDot'
import { LevelBadge } from './LevelBadge'

export interface LabelGrantsPanelProps {
  label: DataLabel | null
  grants: GrantResponse[]
  grantsLoading: boolean
  onAddUser: () => void
  onUpdatePermission: (grant: GrantResponse, permission: LabelPermission) => void
  onRevoke: (grant: GrantResponse) => void
}

export function LabelGrantsPanel({
  label,
  grants,
  grantsLoading,
  onAddUser,
  onUpdatePermission,
  onRevoke,
}: LabelGrantsPanelProps) {
  const { t } = useTranslation()

  if (!label) {
    return (
      <Card className="flex flex-col items-center justify-center py-20 text-muted-foreground">
        <Shield className="h-10 w-10 mb-3 opacity-20" />
        <p className="text-sm">{t('admin.labels.selectLabel')}</p>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader className="border-b">
        <div className="flex items-center gap-3 max-md:flex-wrap max-md:gap-y-1">
          <ColorDot color={label.color} />
          <CardTitle>{label.name}</CardTitle>
          <LevelBadge level={label.level} />
        </div>
        {label.description && (
          <CardDescription className="ml-6">{label.description}</CardDescription>
        )}
      </CardHeader>
      <CardContent className="pt-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            {t('admin.labels.userAccess')}
            <span className="text-muted-foreground/60">({grants.length})</span>
          </h3>
          <Button variant="outline" size="sm" className="h-9 text-xs md:h-7" onClick={onAddUser}>
            <UserPlus className="h-3 w-3 mr-1" />
            {t('admin.labels.addUser')}
          </Button>
        </div>

        {grantsLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : grants.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
            <UserPlus className="h-6 w-6 mb-2 opacity-40" />
            <p className="text-sm">{t('admin.labels.noUsers')}</p>
            <p className="text-xs text-muted-foreground/60 mt-1">{t('admin.labels.noUsersHint')}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {grants.map(grant => (
              <div
                key={grant.id}
                className="flex items-center gap-3 px-3 py-2.5 rounded-md bg-muted/40 border border-border group max-md:flex-wrap max-md:gap-y-2"
              >
                <div className="w-7 h-7 shrink-0 rounded-full bg-muted flex items-center justify-center text-xs font-semibold text-muted-foreground uppercase">
                  {grant.username[0]}
                </div>
                <span className="text-sm text-foreground font-medium flex-1 max-md:min-w-0 max-md:truncate">
                  {grant.username}
                </span>
                <Select
                  value={grant.permission}
                  onValueChange={val => onUpdatePermission(grant, val as LabelPermission)}
                >
                  <SelectTrigger className="w-[140px] h-7 text-xs max-md:order-last max-md:w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="z-[300]">
                    <SelectItem value="read" className="text-xs">
                      {t('admin.labels.permRead')}
                    </SelectItem>
                    <SelectItem value="read_write" className="text-xs">
                      {t('admin.labels.permReadWrite')}
                    </SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 text-muted-foreground hover:text-destructive hover:bg-destructive/10 opacity-0 group-hover:opacity-100 transition-opacity max-md:opacity-100 md:h-7 md:w-7 [@media(hover:none)]:opacity-100"
                  onClick={() => onRevoke(grant)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
